import * as THREE from 'three';
import { createGlowTexture } from '../utils/createGlowTexture.js';

/**
 * InteractiveStar - generic sprite-based interactive star.
 *
 * Used by any star-shaped object that doesn't need a unique custom visual
 * (Family members, future Easter eggs, etc.). Sirius and BirthStar predate
 * this class and use the same pattern inline.
 *
 * Each instance has its own glow texture so colors can vary independently.
 */
export class InteractiveStar {
  constructor({
    position,
    color = 0xffd88c,
    glowColor = '255, 215, 120',
    scale = 32,
    pulseSpeed = 1.4,
    pulseAmount = 0.06,
    initialPhase = 0,
    discoveryId,
    category = 'unknown',
    cardHTML,
    onClick,
  }) {
    this.id = discoveryId;
    this.discoveryId = discoveryId;
    this.category = category;
    this.interactable = true;
    this.onClickCallback = onClick;
    this._cardHTML = cardHTML;
    this._t = initialPhase;
    this._hovering = false;
    this.baseScale = scale;
    this.pulseSpeed = pulseSpeed;
    this.pulseAmount = pulseAmount;

    const texture = createGlowTexture(glowColor);
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: color,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });

    this.mesh = new THREE.Sprite(material);
    this.mesh.position.copy(position);
    this.mesh.scale.setScalar(scale);
    this.mesh.renderOrder = 10;
    this.mesh.userData.star = this;
    this.material = material;
  }

  onHover() {
    document.body.classList.add('star-hover');
    this._hovering = true;
  }

  onUnhover() {
    document.body.classList.remove('star-hover');
    this._hovering = false;
  }

  onClick() {
    if (this.onClickCallback) this.onClickCallback(this);
  }

  cardHTML() {
    return this._cardHTML;
  }

  /**
   * Temporarily pulse this star red for the hint system.
   * Saves the original color, switches to red, amplifies scale,
   * then reverts after `duration` seconds.
   */
  startHint(duration = 15) {
    if (this._hinting) return;
    this._hinting = true;
    this._origColor = this.material.color.getHex();
    this._origScale = this.baseScale;
    this.material.color.setHex(0xff4060);
    this.baseScale = this._origScale * 1.8;
    this._hintTimer = setTimeout(() => {
      this.material.color.setHex(this._origColor);
      this.baseScale = this._origScale;
      this._hinting = false;
    }, duration * 1000);
  }

  update(dt) {
    this._t += dt;
    // Hinting stars pulse faster and stronger to draw attention
    const speed = this._hinting ? 3.0 : this.pulseSpeed;
    const amount = this._hinting ? 0.15 : this.pulseAmount;
    const pulse = 1 + Math.sin(this._t * speed) * amount;
    const targetScale = this._hovering ? this.baseScale * 1.25 : this.baseScale * pulse;
    const cur = this.mesh.scale.x;
    const next = cur + (targetScale - cur) * 0.18;
    this.mesh.scale.setScalar(next);
  }
}
